// frontend-react/src/pages/Home.js
import React, { useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
  CircularProgress,
  Divider,
} from "@mui/material";
import BlogCard from "../components/Blog/BlogCard";
import { AnimateOnScroll } from '../hooks/useScrollAnimate';
import ModernHero from "../components/Home/ModernHero";
import ExternalNews from "../components/Home/ExternalNews";
import CategoryNav from "../components/Home/CategoryNav";
import AdBanner from "../components/Ads/AdBanner";
import { blogService, categoryService } from "../api/services";
import { safeArray } from "../utils/validation";
import { TrendingUp, Whatshot, Newspaper } from '@mui/icons-material';

const Home = () => {
  const [feedData, setFeedData] = useState({ featured: [], latest: [] });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const initHome = async () => {
      try {
        setLoading(true);
        const [catRes, feedRes] = await Promise.all([
          categoryService.getCategories(),
          blogService.getSmartFeed()
        ]);

        setCategories(safeArray(catRes.data));
        setFeedData({
          featured: safeArray(feedRes.data.featured),
          latest: safeArray(feedRes.data.latest)
        });
      } catch (error) {
        console.error("Error initializing Home:", error);
      } finally {
        setLoading(false);
      }
    };
    initHome();
  }, []);

  const { featured: featuredPosts, latest: feedPosts } = feedData;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Category Navigation */}
      <CategoryNav categories={categories} />

      {/* Ad Banner - Top */}
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <AdBanner format="horizontal" placement="home_top_banner" />
      </Container>

      {/* Modern Hero Section */}
      {featuredPosts.length > 0 && (
        <AnimateOnScroll>
          <ModernHero posts={featuredPosts.slice(0, 5)} />
        </AnimateOnScroll>
      )}

      <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
        {/* Categories Navigation */}
        <AnimateOnScroll delay={0.2}>
          <CategoryNav categories={categories} />
        </AnimateOnScroll>

        <Grid container spacing={4}>
          {/* Main Content Column */}
          <Grid item xs={12} lg={8}>
            
            {/* Latest News Section */}
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Whatshot sx={{ fontSize: 40, color: theme.palette.secondary.main }} />
                <Typography 
                  variant="h3" 
                  fontWeight="800"
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Últimas Noticias
                </Typography>
              </Box>

              <AnimateOnScroll delay={0.3}>
                <Grid container spacing={3}>
                  {feedPosts.slice(0, 6).map((post, index) => (
                    <Grid item xs={12} md={6} key={post.id || index}>
                      <AnimateOnScroll delay={index * 0.1}>
                        <BlogCard post={post} />
                      </AnimateOnScroll>
                    </Grid>
                  ))}
                </Grid>
              </AnimateOnScroll>
            </Box>

            {/* Ad Banner - After Latest News */}
            <Box sx={{ my: 6 }}>
              <AdBanner format="horizontal" placement="home_after_latest" />
            </Box>

            <Divider sx={{ my: 8 }} />

            {/* More Stories Section */}
            {feedPosts.length > 6 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                  <Newspaper sx={{ fontSize: 40, color: theme.palette.primary.main}} />
                  <Typography variant="h3" fontWeight="800">
                    Más Historias
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                    {feedPosts.slice(6, 15).map((post, index) => {
                      // Insert ad every 6 posts
                      const shouldShowAd = index > 0 && index % 6 === 0;
                      
                      return (
                        <Fragment key={post.id}>
                          {shouldShowAd && (
                            <Grid item xs={12}>
                              <AdBanner format="horizontal" placement={`home_more_stories_${index}`} />
                            </Grid>
                          )}
                          <Grid item xs={12} md={6} lg={4}>
                            <BlogCard post={post} />
                          </Grid>
                        </Fragment>
                      );
                    })}
                  </Grid>

                {/* View All Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  <Button
                    component={Link}
                    to="/blog"
                    variant="outlined"
                    size="large"
                    sx={{
                      borderRadius: '50px',
                      px: 6,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Ver Todas las Noticias
                  </Button>
                </Box>
              </Box>
            )}

            {/* Ad Banner - Bottom of Main Content */}
            <Box sx={{ mt: 8 }}>
              <AdBanner format="horizontal" placement="home_bottom_main" />
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <TrendingUp sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                  <Typography variant="h5" fontWeight="700">
                    Tendencias
                  </Typography>
                </Box>
                <ExternalNews />

                {/* Ad Banner - Sidebar Top */}
                <Box sx={{ mt: 4 }}>
                  <AdBanner format="rectangle" placement="home_sidebar_top" />
                </Box>
              </Box>

              {/* Newsletter Card */}
              <Box>
                <Box
                  sx={{
                    mt: 6,
                    p: 4,
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 200,
                      height: 200,
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  <Typography variant="h5" fontWeight="800" gutterBottom sx={{ position: 'relative', zIndex: 1 }}>
                    📬 Newsletter
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.95, position: 'relative', zIndex: 1 }}>
                    Recibe las mejores noticias de tecnología directamente en tu correo.
                  </Typography>
                  <Box
                    component="form"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <input
                      type="email"
                      placeholder="tu@email.com"
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: 'none',
                        fontSize: '1rem',
                        outline: 'none',
                      }}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: '#fff',
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                        }
                      }}
                    >
                      Suscribirme
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Ad Banner - Sidebar Bottom */}
              <Box sx={{ mt: 4 }}>
                <AdBanner format="rectangle" placement="home_sidebar_bottom" />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Home;
